import clsx from 'clsx'
import { AlertCircle, CheckCircle2, Info, TriangleAlert, X } from 'lucide-react'
import React from 'react'

const variants = {
  info: {
    container: 'border-primary-200 bg-primary-50/60 dark:border-primary-500/30 dark:bg-primary-950/40',
    accent: 'bg-primary-500',
    icon: 'text-primary-600 dark:text-primary-400',
    title: 'text-primary-900 dark:text-primary-100',
    dismiss:
      'text-primary-500 hover:text-primary-700 hover:bg-primary-100 dark:text-primary-400 dark:hover:text-primary-200 dark:hover:bg-primary-800/50',
  },
  warning: {
    container: 'border-orange-200 bg-orange-50/60 dark:border-orange-500/30 dark:bg-orange-950/40',
    accent: 'bg-orange-500',
    icon: 'text-orange-600 dark:text-orange-400',
    title: 'text-orange-900 dark:text-orange-100',
    dismiss:
      'text-orange-500 hover:text-orange-700 hover:bg-orange-100 dark:text-orange-400 dark:hover:text-orange-200 dark:hover:bg-orange-800/50',
  },
  error: {
    container: 'border-red-200 bg-red-50/60 dark:border-red-500/30 dark:bg-red-950/40',
    accent: 'bg-red-500',
    icon: 'text-red-600 dark:text-red-400',
    title: 'text-red-900 dark:text-red-100',
    dismiss:
      'text-red-500 hover:text-red-700 hover:bg-red-100 dark:text-red-400 dark:hover:text-red-200 dark:hover:bg-red-800/50',
  },
  success: {
    container: 'border-green-200 bg-green-50/60 dark:border-green-500/30 dark:bg-green-950/40',
    accent: 'bg-green-500',
    icon: 'text-green-600 dark:text-green-400',
    title: 'text-green-900 dark:text-green-100',
    dismiss:
      'text-green-500 hover:text-green-700 hover:bg-green-100 dark:text-green-400 dark:hover:text-green-200 dark:hover:bg-green-800/50',
  },
}

const variantIcons = {
  info: Info,
  warning: TriangleAlert,
  error: AlertCircle,
  success: CheckCircle2,
}

export type BannerVariant = keyof typeof variants

type BannerProps = {
  variant?: BannerVariant
  title: string
  description?: string
  fullWidth?: boolean
  onDismiss?: () => void
  icon?: React.ReactNode
  children?: React.ReactNode
  className?: string
}

export const Banner = ({
  variant = 'info',
  title,
  description,
  fullWidth = false,
  onDismiss,
  icon,
  children,
  className,
}: BannerProps) => {
  const styles = variants[variant]
  const DefaultIcon = variantIcons[variant]
  const role = variant === 'error' || variant === 'warning' ? 'alert' : 'status'

  return (
    <div
      role={role}
      className={clsx(
        'relative overflow-hidden border',
        fullWidth ? 'rounded-none' : 'rounded-lg',
        styles.container,
        className,
      )}
    >
      {/* Left accent stripe */}
      <div
        aria-hidden="true"
        className={clsx('absolute inset-y-0 left-0 w-1', styles.accent)}
      />

      <div className="flex items-start gap-3 py-4 pr-4 pl-5">
        {/* Icon */}
        <div
          aria-hidden="true"
          className={clsx('mt-0.5 shrink-0', styles.icon)}
        >
          {icon ?? <DefaultIcon className="size-4" />}
        </div>

        {/* Content */}
        <div className="min-w-0 flex-1">
          <p className={clsx('text-sm font-semibold', styles.title)}>{title}</p>
          {description && <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{description}</p>}
          {children && <div className="mt-3 flex items-center gap-2">{children}</div>}
        </div>

        {/* Dismiss button */}
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Dismiss"
            className={clsx(
              'shrink-0 rounded-md p-1 transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1',
              styles.dismiss,
            )}
          >
            <X
              aria-hidden="true"
              className="size-4"
            />
          </button>
        )}
      </div>
    </div>
  )
}
